import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, RefreshCw, Wrench, CheckCircle, AlertTriangle, History,
} from "lucide-react";

const DEFECT_META = [
  { key: "plating_ng", label: "Plating NG", accent: "amber" },
  { key: "burr_rework", label: "Burr (rework)", accent: "amber" },
];

const ReworkQueue = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeLogVi, setActiveLogVi] = useState(null); // { visual_id, defect_key }
  const [logRecovered, setLogRecovered] = useState(0);
  const [logScrapped, setLogScrapped] = useState(0);
  const [logRemarks, setLogRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchQueue = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/rework-queue?include_closed=${includeClosed}`);
      setItems(res.data.items || []);
      setOpenCount(res.data.open_count || 0);
      setTotalPending(res.data.total_pending_qty || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load rework queue");
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get("/rework-queue/history?limit=100");
      setHistory(res.data.records || []);
    } catch (err) {
      console.error("Rework history load failed", err);
    }
  };

  useEffect(() => {
    fetchQueue();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeClosed]);

  const openLog = (visual_id, defect_key, pending) => {
    setActiveLogVi({ visual_id, defect_key, pending });
    setLogRecovered(0);
    setLogScrapped(0);
    setLogRemarks("");
    setError("");
  };

  const cancelLog = () => {
    setActiveLogVi(null);
  };

  const submitLog = async () => {
    if (!activeLogVi) return;
    const rec = Number(logRecovered) || 0;
    const scr = Number(logScrapped) || 0;
    if (rec + scr === 0) {
      setError("Enter at least one non-zero quantity");
      return;
    }
    if (rec + scr > activeLogVi.pending) {
      setError(`Total (${rec + scr}) exceeds pending (${activeLogVi.pending})`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/rework-queue/log", {
        visual_id: activeLogVi.visual_id,
        defect_key: activeLogVi.defect_key,
        qty_recovered: rec,
        qty_scrapped_after_rework: scr,
        remarks: logRemarks,
      });
      setActiveLogVi(null);
      fetchQueue();
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to log rework");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Header />
      <div className="bg-[#1A3560] text-white px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="rq-back-btn"
              onClick={() => navigate("/home")}
              className="bg-white/10 hover:bg-white/20 rounded-lg p-2"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg font-bold">Rework Queue</h1>
              <p className="text-[11px] text-white/60">पुनर्कार्य कतार · Log recovery of reworkable defects</p>
            </div>
          </div>
          <button
            data-testid="rq-history-btn"
            onClick={() => setShowHistory(!showHistory)}
            className="bg-white/10 hover:bg-white/20 text-xs font-bold px-3 py-2 rounded-lg"
          >
            {showHistory ? "Hide" : "Log"} ({history.length})
          </button>
        </div>
        <div className="max-w-2xl mx-auto flex items-center gap-3 mt-3">
          <div className="bg-white/10 rounded-lg px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Open items</p>
            <p className="font-extrabold text-xl">{openCount}</p>
          </div>
          <div className="bg-white/10 rounded-lg px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Pending qty</p>
            <p className="font-extrabold text-xl">{totalPending}</p>
          </div>
          <label className="flex items-center gap-2 text-xs bg-white/10 rounded-lg px-3 py-2 cursor-pointer">
            <input
              data-testid="rq-toggle-closed"
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            Show closed
          </label>
          <button
            data-testid="rq-refresh-btn"
            onClick={fetchQueue}
            className="bg-white/10 hover:bg-white/20 rounded-lg p-2 ml-auto"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {error && !activeLogVi && (
        <div className="max-w-2xl mx-auto mt-3 px-4">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-2" />
            <p className="font-bold text-slate-900">No rework pending</p>
            <p className="text-xs text-slate-400 mt-1">
              {includeClosed
                ? "There are no visual inspections with reworkable defects."
                : "All logged rework is complete."}
            </p>
          </div>
        ) : items.map((it) => <QueueCard key={it.visual_id} item={it} onOpenLog={openLog} />)}

        {showHistory && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-4">
            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Recent rework logs ({history.length})</p>
              <button onClick={fetchHistory} className="text-slate-400 hover:text-slate-700"><History size={12} /></button>
            </div>
            {history.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">No rework logged yet.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                {history.map((h) => (
                  <div key={`${h.visual_id}-${h.logged_at}`} className="p-3 text-xs">
                    <div className="flex justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-[10px] text-slate-400 break-all">{h.fg_batch_no}</p>
                        <p className="font-bold text-slate-800">{h.part_name}</p>
                        <p className="text-slate-500 text-[11px]">{h.defect_label} · {h.logged_by_name}</p>
                        {h.remarks && <p className="text-slate-500 italic mt-0.5">"{h.remarks}"</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {h.qty_recovered > 0 && <p className="font-bold text-emerald-700">+{h.qty_recovered} OK</p>}
                        {h.qty_scrapped_after_rework > 0 && <p className="font-bold text-red-700">-{h.qty_scrapped_after_rework} scrap</p>}
                        <p className="text-[10px] text-slate-400">{(h.logged_at || "").slice(0, 16).replace("T", " ")}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Log modal */}
      {activeLogVi && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={cancelLog}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Log rework for {activeLogVi.defect_key === "plating_ng" ? "Plating NG" : "Burr (rework)"}
            </p>
            <p className="text-[11px] font-mono text-slate-500 mt-1">{activeLogVi.visual_id}</p>
            <p className="text-xs text-slate-500 mt-1">Pending: <b className="text-amber-700">{activeLogVi.pending} pc(s)</b></p>

            {error && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-xs flex items-start gap-2">
                <AlertTriangle size={12} className="mt-0.5" /><p>{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-[11px] uppercase font-bold tracking-widest text-emerald-700">Recovered (back to OK)</label>
                <input
                  data-testid="rq-log-recovered"
                  type="number"
                  min="0"
                  value={logRecovered}
                  onChange={(e) => setLogRecovered(e.target.value.replace(/[^0-9]/g, ""))}
                  className="mt-1 w-full h-14 text-2xl font-extrabold text-center text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold tracking-widest text-red-700">Scrap (rework failed)</label>
                <input
                  data-testid="rq-log-scrapped"
                  type="number"
                  min="0"
                  value={logScrapped}
                  onChange={(e) => setLogScrapped(e.target.value.replace(/[^0-9]/g, ""))}
                  className="mt-1 w-full h-14 text-2xl font-extrabold text-center text-red-700 bg-red-50 border-2 border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
            </div>

            <label className="mt-3 block text-[11px] uppercase font-bold tracking-widest text-slate-400">Remarks (optional)</label>
            <textarea
              data-testid="rq-log-remarks"
              value={logRemarks}
              onChange={(e) => setLogRemarks(e.target.value)}
              rows={2}
              placeholder="e.g., re-plated batch #4"
              className="mt-1 w-full p-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3560] resize-none"
            />

            <div className="flex gap-2 mt-4">
              <button
                data-testid="rq-log-cancel"
                onClick={cancelLog}
                className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="rq-log-submit"
                onClick={submitLog}
                disabled={submitting}
                className="flex-1 h-11 bg-[#15803D] text-white text-sm font-bold rounded-xl hover:bg-[#0F5D2D] disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Log rework"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QueueCard = ({ item, onOpenLog }) => {
  return (
    <div data-testid={`rq-item-${item.visual_id}`} className={`bg-white rounded-2xl border ${item.closed ? "border-emerald-200" : "border-slate-200"} overflow-hidden`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-mono text-slate-400 break-all">{item.fg_batch_no || "—"}</p>
            <p className="font-bold text-slate-900 truncate">{item.part_name || "(unnamed)"}</p>
            <p className="text-[11px] font-mono text-slate-400">{item.part_no || "—"}</p>
            <p className="text-[10px] text-slate-500 mt-1">
              Inspected: {item.inspector_name} · {(item.submitted_at || "").split("T")[0]}
            </p>
          </div>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${item.closed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {item.closed ? "CLOSED" : `${item.pending_total} pending`}
          </span>
        </div>

        <div className="mt-4 space-y-2">
          {DEFECT_META.map((d) => {
            const originalQty = item.reworkable?.[d.key] || 0;
            if (originalQty === 0) return null;
            const recovered = item.rework_recovered?.[d.key] || 0;
            const scrapped = item.rework_scrapped?.[d.key] || 0;
            const pending = item.pending?.[d.key] || 0;
            return (
              <div key={d.key} className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Wrench size={12} className="text-amber-700" /> {d.label}
                  </p>
                  {pending > 0 ? (
                    <button
                      data-testid={`rq-log-${item.visual_id}-${d.key}`}
                      onClick={() => onOpenLog(item.visual_id, d.key, pending)}
                      className="bg-[#1A3560] hover:bg-[#102340] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg"
                    >
                      Log
                    </button>
                  ) : (
                    <CheckCircle size={14} className="text-emerald-600" />
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
                  <StatChip label="Total" value={originalQty} colour="slate" />
                  <StatChip label="Recovered" value={recovered} colour="emerald" />
                  <StatChip label="Scrapped" value={scrapped} colour="red" />
                  <StatChip label="Pending" value={pending} colour="amber" highlight={pending > 0} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const StatChip = ({ label, value, colour, highlight = false }) => {
  const colourMap = {
    slate: "text-slate-700",
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div className={`rounded-lg py-1.5 ${highlight ? "bg-amber-100" : "bg-white"}`}>
      <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className={`font-extrabold ${colourMap[colour]}`}>{value}</p>
    </div>
  );
};

export default ReworkQueue;
